import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["search", "results", "selected", "hiddenInput", "form"]
  static values = {
    searchUrl: String,
    excludeIds: Array
  }

  connect() {
    this.searchTimeout = null
    this.selectedBook = null
  }

  search() {
    const query = this.searchTarget.value.trim()

    clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
      return
    }

    this.searchTimeout = setTimeout(() => this.performSearch(query), 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const books = await response.json()
      this.renderResults(books)
    } catch (error) {
      console.error("Book search error:", error)
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-red-600">Search failed. Please try again.</div>`
      this.resultsTarget.classList.remove("hidden")
    }
  }

  renderResults(books) {
    // Filter out already featured books
    const filteredBooks = books.filter(book => !this.excludeIdsValue.includes(book.id))

    if (filteredBooks.length === 0) {
      this.resultsTarget.innerHTML = `<div class="p-3 text-sm text-beige-500 font-fira">No books found</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const html = filteredBooks.map(book => `
      <button type="button"
              class="w-full flex items-center gap-3 p-2 hover:bg-beige-50 rounded-lg text-left transition-colors"
              data-action="featured-book-selector#select"
              data-book-id="${book.id}"
              data-book-title="${this.escapeAttr(book.title)}"
              data-book-author="${this.escapeAttr(book.author_names || '')}"
              data-book-cover="${this.escapeAttr(book.cover_url || '')}">
        <div class="w-10 h-14 flex-shrink-0 bg-beige-200 rounded overflow-hidden">
          ${book.cover_url ? `<img src="${this.escapeAttr(book.cover_url)}" alt="" class="w-full h-full object-cover">` : ''}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(book.title)}</p>
          ${book.author_names ? `<p class="text-xs text-beige-500 font-fira truncate">${this.escapeHtml(book.author_names)}</p>` : ''}
        </div>
      </button>
    `).join("")

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  select(event) {
    const button = event.currentTarget

    const book = {
      id: parseInt(button.dataset.bookId),
      title: button.dataset.bookTitle,
      author_names: button.dataset.bookAuthor,
      cover_url: button.dataset.bookCover
    }

    this.selectedBook = book
    this.hiddenInputTarget.value = book.id

    // Show selected book
    this.selectedTarget.innerHTML = `
      <div class="flex items-center gap-3 p-3 bg-beige-50 rounded-lg">
        <div class="w-12 h-16 flex-shrink-0 bg-beige-200 rounded overflow-hidden">
          ${book.cover_url ? `<img src="${this.escapeAttr(book.cover_url)}" alt="" class="w-full h-full object-cover">` : ''}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-beige-900 truncate">${this.escapeHtml(book.title)}</p>
          ${book.author_names ? `<p class="text-xs text-beige-500 font-fira truncate">${this.escapeHtml(book.author_names)}</p>` : ''}
        </div>
        <button type="button"
                class="p-1 text-beige-400 hover:text-red-600 transition-colors"
                data-action="featured-book-selector#clear"
                title="Clear selection">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `

    // Hide search
    this.searchTarget.classList.add("hidden")
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")

    // Show form fields
    this.formTarget.classList.remove("hidden")
  }

  clear() {
    this.selectedBook = null
    this.hiddenInputTarget.value = ""
    this.selectedTarget.innerHTML = ""
    this.searchTarget.value = ""
    this.searchTarget.classList.remove("hidden")
    this.formTarget.classList.add("hidden")
  }

  hideResults(event) {
    if (this.resultsTarget.contains(event.relatedTarget)) return

    setTimeout(() => {
      this.resultsTarget.classList.add("hidden")
    }, 200)
  }

  escapeHtml(text) {
    if (!text) return ""
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  escapeAttr(text) {
    if (!text) return ""
    return text.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
